import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Card,
  CardTitle,
  Col,
  Container,
  FormFeedback,
  Input,
  Row,
  Table,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppMeta from 'components/App/Meta';
import SelectValidation from 'components/App/SelectValidation';
import FormField from 'components/AddLocation/FormField';
import InputValidation from 'components/App/InputValidation';

import { ReactComponent as AddButtonSVG } from 'assets/images/plus.svg';
import {
  LOCATIONS, OPTION_AGES, OPTION_AGES_EXTRA_BED, ROOM_TYPE,
} from 'constants/index';
import CHANGE_QUANTITY_STATUS from 'constants/changeQuantityStatus';
import { parseCurrency, unparseCurrency } from 'utils';
import randomIDString from 'utils/randomId';
import { updateChildExtraBedPolicy } from 'utils/validate';

import styles from './EditChildExtraBedPolicy.module.scss';

const { INCREASE, REDUCE } = CHANGE_QUANTITY_STATUS;

function EditChildExtraBedPolicy(props) {
  const {
    isLoadingUpdateChildrenAndExtraBedPolicy,

    roomOfPlaceList,
    placeDetail,

    actionGetPlaceDetail,
    actionGetListRoomsOfPlace,
    actionUpdateChildrenAndExtraBedPolicy,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const breadcrumbList = React.useMemo(() => ([
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`,
    },
    {
      name: 'Chính sách',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.POLICY}?place=${placeId}`,
    },
    {
      name: 'Chính sách trẻ em và giường phụ',
      isActive: true,
      link: '',
    },
  ]), [placeId]);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      allowChildrenPolicy: false,
      allowExtraBedPolicy: false,
      childrenPolicy: [],
      extraBedPolicy: [],
    },

    validationSchema: Yup.object(updateChildExtraBedPolicy),

    onSubmit: (values) => {
      const formatData = {
        ...values,
        childrenPolicy: values.allowChildrenPolicy ? values.childrenPolicy.map((item) => {
          switch (item.condition) {
            case OPTION_AGES.FROM.value:
              return ({
                condition: item.condition,
                price: parseCurrency(item.price),
                childFrom: +item.childFrom,
                childTo: +item.childTo,
              });

            case OPTION_AGES.ABOVE.value:
              return ({
                condition: item.condition,
                price: parseCurrency(item.price),
                childFrom: +item.childFrom,
              });

            default:
              return ({
                condition: item.condition,
                price: parseCurrency(item.price),
                childTo: +item.childTo,
              });
          }
        }) : [],
        extraBedPolicy: values.allowExtraBedPolicy ? values.extraBedPolicy
          .filter((item) => item.isChecked).map((item) => {
            if (item.ageLimitCondition === 'FROM') {
              return ({
                room: typeof item.room === 'object' ? item.room._id : item.room,
                bedQuantity: item.bedQuantity,
                price: parseCurrency(item.price),
                ageLimitCondition: item.ageLimitCondition,
                childFrom: +item.childFrom,
                childTo: +item.childTo,
              });
            }
            return ({
              room: typeof item.room === 'object' ? item.room._id : item.room,
              bedQuantity: item.bedQuantity,
              price: parseCurrency(item.price),
              ageLimitCondition: item.ageLimitCondition,
            });
          }) : [],
      };

      if (!values.allowExtraBedPolicy) delete formatData.extraBedPolicy;

      actionUpdateChildrenAndExtraBedPolicy({
        placeId,
        data: formatData,
      });
    },
  });

  const onClickCancel = React.useCallback(() => {
    history.push(`${LOCATIONS.LOCATION_DETAIL.POLICY}?place=${placeId}`);
  }, [history, placeId]);

  const onAddChildrenPolicy = React.useCallback(() => {
    const newChildrenPolicy = {
      id: randomIDString(),
      condition: OPTION_AGES.BELOW.value,
      price: '0',
      childFrom: '',
      childTo: '',
    };

    validation.setValues((values) => ({
      ...values,
      childrenPolicy: [...values.childrenPolicy, newChildrenPolicy],
    }));
  }, []);

  const onDeleteChildrenPolicy = React.useCallback((rowId) => () => {
    validation.setValues((values) => ({
      ...values,
      childrenPolicy: values.childrenPolicy.filter((item) => item.id !== rowId),
    }));
  }, []);

  const onChangeField = React.useCallback((field, rowId) => (e) => {
    validation.setValues((values) => ({
      ...values,
      childrenPolicy: values.childrenPolicy.map((item) => {
        if (item.id === rowId) {
          return {
            ...item,
            [field]: field === 'condition' ? e.value : e.target.value,
          };
        }

        return item;
      }),
    }));
  }, []);

  const onChangeAgeRangeBedTypes = React.useCallback((roomId) => (e) => {
    const updatedBedTypes = validation.values.extraBedPolicy.map((item) => {
      if (item.room === roomId) {
        return {
          ...item,
          [e.target.name]: e.target.value,
        };
      }
      return item;
    });

    validation.setFieldValue('extraBedPolicy', updatedBedTypes, false);
  }, [validation]);

  const onChangeCheckBox = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      extraBedPolicy: values.extraBedPolicy.map((item) => {
        if (item.room === e.target.value) {
          return {
            ...item,
            isChecked: e.target.checked,
          };
        }
        return item;
      }),
    }));
  }, []);

  // eslint-disable-next-line no-unused-vars
  const onChangeQuantity = React.useCallback((id, action) => () => {
    const updatedBedTypes = validation.values.extraBedPolicy.map((item) => {
      if (item.room === id) {
        let newBedQuantity = item.bedQuantity;

        if (action === INCREASE) {
          newBedQuantity += 1;
        } else if (action === REDUCE) {
          newBedQuantity = Math.max(0, newBedQuantity - 1);
        }

        return {
          ...item,
          bedQuantity: newBedQuantity,
        };
      }

      return item;
    });

    validation.setFieldValue('extraBedPolicy', updatedBedTypes);
  }, [validation]);

  const onSelectAll = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      extraBedPolicy: values.extraBedPolicy.map((item) => ({
        ...item,
        isChecked: e.target.checked,
      })),
    }));
  }, []);

  // eslint-disable-next-line no-unused-vars
  const onChangeAgeRange = React.useCallback((roomId) => (e) => {
    const updatedBedTypes = validation.values.extraBedPolicy.map((item) => {
      if (item.room === roomId) {
        return {
          ...item,
          ageLimitCondition: e.value,
        };
      }

      return item;
    });

    validation.setFieldValue('extraBedPolicy', updatedBedTypes);
  }, [validation]);

  const onChangeRadio = React.useCallback((e) => {
    validation.setFieldValue(e.target.name, !!+e.target.value);
  }, []);

  const onChangeBedQuantity = React.useCallback((e) => {
    const formatNumber = parseInt(e.target.value || 0, 10);
    const targetId = e.target.id;

    validation.setValues((values) => ({
      ...values,
      extraBedPolicy: values.extraBedPolicy.map((item) => {
        const roomId = typeof item.room === 'string' ? item.room : item.room?._id;
        if (roomId === targetId || item.room === targetId) {
          return {
            ...item,
            bedQuantity: formatNumber > 99 ? 99 : formatNumber,
          };
        }
        return item;
      }),
    }));
  }, []);

  React.useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({ placeId });
      actionGetListRoomsOfPlace({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    if (Object.keys(placeDetail).length > 0) {
      const childrenPolicyFormated = placeDetail.childrenPolicy?.map((item) => ({
        ...item,
        id: randomIDString(),
        childFrom: item.childFrom ? item.childFrom : '',
        childTo: item.childTo ? item.childTo : '',
        price: unparseCurrency(item.price),
      }));

      const defaultchildrenPolicy = [{
        id: randomIDString(),
        condition: OPTION_AGES.BELOW.value,
        price: '0',
        childFrom: '',
        childTo: '',
      }];

      const defaultExtraBedPolicy = roomOfPlaceList.map((room) => ({
        id: randomIDString(),
        room: room._id,
        name: room.name,
        roomName: room.roomName,
        bedQuantity: 0,
        isChecked: false,
        ageLimitCondition: OPTION_AGES_EXTRA_BED.ANY.value,
        price: '0',
      }));

      const extraBedPolicyFormated = placeDetail.extraBedPolicy?.map((item) => ({
        ...item,
        id: randomIDString(),
        childFrom: item.childFrom ? item.childFrom : '',
        childTo: item.childTo ? item.childTo : '',
        isChecked: true,
        name: item.room?.name,
        roomName: item.room?.roomName,
        room: item.room?._id,
        price: unparseCurrency(item.price),
      }));

      const newExtraBedPolicy = defaultExtraBedPolicy.map((item) => {
        const checkHasExtraBed = extraBedPolicyFormated.find(
          (extraBed) => extraBed.room === item.room,
        );

        if (checkHasExtraBed) {
          return checkHasExtraBed;
        }

        return item;
      });

      validation.setValues((values) => ({
        ...values,
        allowChildrenPolicy: placeDetail.allowChildrenPolicy || false,
        allowExtraBedPolicy: placeDetail.allowExtraBedPolicy || false,
        childrenPolicy: placeDetail.childrenPolicy?.length > 0
          ? childrenPolicyFormated : defaultchildrenPolicy,
        extraBedPolicy: newExtraBedPolicy,
      }));
    }
  }, [placeDetail, roomOfPlaceList]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <AppMeta
        name="Chính sách trẻ em và giường phụ"
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="CHÍNH SÁCH TRẺ EM VÀ GIƯỜNG PHỤ"
          breadcrumbList={breadcrumbList}
        />

        <Row>
          <Card className="Section">
            <CardTitle className="Section__Title">
              Bạn có cho trẻ em dưới 18 tuổi lưu trú tại chỗ nghỉ không?
            </CardTitle>
            <div className="Section__Card">
              <label className="Section__Card__Checkbox__Wrapper">
                <Input
                  type="radio"
                  className="Section__Card__Checkbox"
                  name="allowChildrenPolicy"
                  value="1"
                  checked={validation.values.allowChildrenPolicy === true}
                  onChange={onChangeRadio}
                />

                <div className="Section__Card__Checkbox__Name">
                  Có
                </div>
              </label>

              <label className="Section__Card__Checkbox__Wrapper">
                <Input
                  type="radio"
                  className="Section__Card__Checkbox"
                  name="allowChildrenPolicy"
                  value="0"
                  checked={validation.values.allowChildrenPolicy === false}
                  onChange={onChangeRadio}
                />

                <div className="Section__Card__Checkbox__Name">
                  Không
                </div>
              </label>
            </div>
          </Card>
        </Row>

        {validation.values.allowChildrenPolicy && (
        <Row>
          <Card className="Section overflow-auto">
            <CardTitle className="Section__Title">
              Thiết lập giá và giới hạn tuổi của Bạn cho Trẻ em
            </CardTitle>

            <div className="Section__Card">
              <div className="Section__Card__Inputs">
                {validation.values.childrenPolicy.map((item) => (
                  <div className="Section__Card__Inputs__Row" key={item.id}>
                    <FormField label="Tuổi">
                      <div className="Section__Card__Inputs__Row__Content CustomMinWidth">
                        <Row className="w-100">
                          <Col xs={4}>
                            <SelectValidation
                              name="condition"
                              id={item.id}
                              placeholder="Tuổi"
                              options={Object.values(OPTION_AGES)}
                              value={item.condition}
                              onChange={onChangeField('condition', item.id)}
                            />
                          </Col>

                          <Col xs={8} className="d-flex gap-3">
                            {item.condition !== OPTION_AGES.BELOW.value && (
                            <AppInputNumberValidation
                              wrapperClassName="ageNumber"
                              type="number"
                              placeholder="Nhập số tuổi từ"
                              validation={validation}
                              showCurrency="Tuổi"
                              value={item.childFrom}
                              isCustomOnChange
                              onChange={onChangeField('childFrom', item.id)}
                            />
                            )}

                            {item.condition === OPTION_AGES.FROM.value && (
                              <span>đến</span>
                            )}

                            {(item.condition === OPTION_AGES.FROM.value
                            || item.condition === OPTION_AGES.BELOW.value) && (
                              <AppInputNumberValidation
                                wrapperClassName="ageNumber"
                                type="number"
                                placeholder="Nhập số tuổi đến"
                                validation={validation}
                                showCurrency="Tuổi"
                                value={item.childTo}
                                isCustomOnChange
                                onChange={onChangeField('childTo', item.id)}
                              />
                            )}
                          </Col>
                        </Row>
                      </div>
                    </FormField>
                    <FormField label="Giá">
                      <div className="Section__Card__Inputs__Row__Content">
                        {item.condition === OPTION_AGES.ABOVE.value ? (
                          <InputValidation
                            value="Phụ thu như người lớn"
                            validation={validation}
                            wrapperClassName="InputPrice"
                            isDisabled
                          />
                        ) : (
                          <AppInputNumberValidation
                            type="number"
                            placeholder="Nhập giá tiền"
                            validation={validation}
                            showCurrency="VNĐ"
                            value={item.price}
                            isCustomOnChange
                            onChange={onChangeField('price', item.id)}
                            wrapperClassName="InputPrice"
                          />
                        )}

                        <AppButton
                          isDefault
                          classes="Button__Delete"
                          onClick={onDeleteChildrenPolicy(item.id)}
                        >
                          Xóa
                        </AppButton>
                      </div>
                    </FormField>
                  </div>
                ))}
                {validation.errors?.childrenPolicy && validation.touched.childrenPolicy && (
                  <FormFeedback type="invalid" className="d-block font-size-12 mb-3">
                    {validation.errors.childrenPolicy}
                  </FormFeedback>
                )}
                {validation.values.childrenPolicy.length < 10 && (
                <AppButton
                  classes="Button__Add"
                  onClick={onAddChildrenPolicy}
                >
                  <AddButtonSVG />
                  Thêm
                </AppButton>
                )}
              </div>
            </div>

            <div className="Section__Policy">
              <Col md="5" className="Section__Policy__Title">
                Tóm tắt chính sách
              </Col>

              <ul className="Section__Policy__Information__Wrapper">
                {validation.values.childrenPolicy.map((item) => {
                  switch (true) {
                    case item.condition === OPTION_AGES.BELOW.value && +item.price === 0:
                      return (
                        <li key={item._id} className="Section__Policy__Information__Content">
                          {`Trẻ em dưới ${item.childTo} tuổi được miễn phí phụ thu khi dùng giường có sẵn`}
                        </li>
                      );

                    case item.condition === OPTION_AGES.BELOW.value && +item.price !== 0:
                      return (
                        <li key={item._id} className="Section__Policy__Information__Content">
                          {`Trẻ em dưới ${item.childTo} tuổi sẽ phụ thu ${unparseCurrency(item.price)} VNĐ/ trẻ/ phòng/ đêm`}
                        </li>
                      );

                    case item.condition === OPTION_AGES.FROM.value && +item.price === 0:
                      return (
                        <li key={item._id} className="Section__Policy__Information__Content">
                          {`Trẻ em từ ${item.childFrom} - ${item.childTo} tuổi được miễn phí phụ thu khi dùng giường có sẵn`}
                        </li>
                      );

                    case item.condition === OPTION_AGES.FROM.value && +item.price !== 0:
                      return (
                        <li key={item._id} className="Section__Policy__Information__Content">
                          {`Trẻ em từ ${item.childFrom} - ${item.childTo} tuổi sẽ phụ thu ${unparseCurrency(item.price)} VNĐ/ trẻ/ phòng/ đêm`}
                        </li>
                      );
                    case item.condition === OPTION_AGES.ABOVE.value:
                      return (
                        <li key={item._id} className="Section__Policy__Information__Content">
                          {`Trẻ em trên ${item.childFrom} tuổi trở lên phụ thu như người lớn`}
                        </li>
                      );

                    default:
                      return null;
                  }
                })}
              </ul>
            </div>
          </Card>
        </Row>
        )}

        <Row>
          <Card className="Section">
            <CardTitle className="Section__Title">
              Bạn có muốn thêm giường phụ không?
            </CardTitle>
            <div className="Section__Card">
              <label className="Section__Card__Checkbox__Wrapper">
                <Input
                  type="radio"
                  className="Section__Card__Checkbox"
                  name="allowExtraBedPolicy"
                  value="1"
                  checked={validation.values.allowExtraBedPolicy === true}
                  onChange={onChangeRadio}
                />

                <div className="Section__Card__Checkbox__Name">
                  Có
                </div>
              </label>

              <label className="Section__Card__Checkbox__Wrapper">
                <Input
                  type="radio"
                  className="Section__Card__Checkbox"
                  name="allowExtraBedPolicy"
                  value="0"
                  checked={validation.values.allowExtraBedPolicy === false}
                  onChange={onChangeRadio}
                />

                <div className="Section__Card__Checkbox__Name">
                  Không
                </div>
              </label>
            </div>
          </Card>
        </Row>
        {validation.values.allowExtraBedPolicy && (
          <Row>
            <Card className="Section overflow-auto">
              <div>
                <CardTitle className="Section__Title mb-1">
                  Chính sách giường phụ
                </CardTitle>
                <div className="text-muted font-size-13">
                  Phụ thu người lớn và giường phụ
                </div>
              </div>

              <div className="Section__Card">
                <Table
                  className="mb-0 Table align-middle"
                >
                  <thead>
                    <tr>
                      <th className="Table__Header__Title Checkbox">
                        <Input
                          id="selectAllCheckbox"
                          className="cursor-pointer"
                          type="checkbox"
                          checked={validation.values.extraBedPolicy?.every(
                            (item) => item.isChecked,
                          )}
                          onChange={onSelectAll}
                        />
                      </th>

                      <th className="Table__Header__Title Room">
                        Phòng
                      </th>

                      <th className="Table__Header__Title Quantity">
                        Số lượng giường phụ
                      </th>

                      <th className="Table__Header__Title Price">
                        Giá
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {validation.values.extraBedPolicy?.map((item) => (
                      <tr
                        key={typeof item.room === 'string' ? item.room : item.room._id}
                        className="Table__Body__Row"
                      >
                        <th scope="row">
                          <Input
                            id={typeof item.room === 'string' ? item.room : item.room._id}
                            value={typeof item.room === 'string' ? item.room : item.room._id}
                            className="cursor-pointer"
                            type="checkbox"
                            checked={item.isChecked}
                            onChange={onChangeCheckBox}
                          />
                        </th>

                        <td>
                          {item.name || ROOM_TYPE[item.roomName]?.label}
                        </td>

                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                        <td>
                          <input
                            type="number"
                            name="bedQuantity"
                            id={typeof item.room === 'string' ? item.room : item.room._id}
                            className="form-control"
                            style={{ width: '90px', borderRadius: '6px' }}
                            value={item.bedQuantity.toString()}
                            placeholder="0"
                            onChange={onChangeBedQuantity}
                            min="0"
                          />
                        </td>

                        <td style={{ maxWidth: '240px' }}>
                          <AppInputNumberValidation
                            name="price"
                            type="number"
                            placeholder="Nhập giá"
                            validation={validation}
                            showCurrency="VNĐ"
                            value={item.price}
                            isCustomOnChange
                            onChange={onChangeAgeRangeBedTypes(
                              typeof item.room === 'string' ? item.room : item.room._id,
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {validation.errors.extraBedPolicy && validation.touched.extraBedPolicy && (
                <FormFeedback type="invalid" className="d-block font-size-12 mb-3">
                  {validation.errors.extraBedPolicy}
                </FormFeedback>
              )}
            </Card>
          </Row>
        )}

        <Row>
          <Card className="Card__Button">
            <div className="Card__Button__Container">
              <div className="Card__Button__Wrapper">
                <AppButton classes="Button Button__Cancel" onClick={onClickCancel}>
                  Hủy
                </AppButton>

                <AppButton classes="Button Button__Save" onClick={validation.handleSubmit} loading={isLoadingUpdateChildrenAndExtraBedPolicy}>
                  Cập nhật
                </AppButton>
              </div>
            </div>
          </Card>
        </Row>
      </Container>
    </div>
  );
}

EditChildExtraBedPolicy.propTypes = {
  isLoadingUpdateChildrenAndExtraBedPolicy: PropTypes.bool.isRequired,

  roomOfPlaceList: PropTypes.instanceOf(Array).isRequired,
  placeDetail: PropTypes.instanceOf(Object).isRequired,

  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionGetListRoomsOfPlace: PropTypes.func.isRequired,
  actionUpdateChildrenAndExtraBedPolicy: PropTypes.func.isRequired,
};

export default EditChildExtraBedPolicy;
